import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice de titration chimique - Calcul de concentration et de volume en opérations de titrage";
const DESCRIPTION =
  "Calculatrice de titration chimique - Calcul de concentration et de volume en opérations de titrage. Calculateur de chimie en francais pour cours et TP, avec saisie simple, resultat rapide et interpretation claire.";
const KEYWORDS =
  "titration, calculatrice chimie, formule de chimie, tp de chimie, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/titration/";

const SCHEMA = [
  {
    "@context": "https://schema.org", 
    "@type": "WebApplication",
    name: "Calculatrice de titration chimique",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "EducationalApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
];

function TitrationCalculator() {
  const [solveFor, setSolveFor] = useState("M2");
  const [m1, setM1] = useState("");
  const [v1, setV1] = useState("");
  const [m2, setM2] = useState("");
  const [v2, setV2] = useState("");
  const [n1, setN1] = useState("1");
  const [n2, setN2] = useState("1");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculate = () => {
    try {
      setError("");
      const m1Val = parseFloat(m1) || 0;
      const v1Val = parseFloat(v1) || 0;
      const m2Val = parseFloat(m2) || 0;
      const v2Val = parseFloat(v2) || 0;
      const n1Val = parseFloat(n1) || 1;
      const n2Val = parseFloat(n2) || 1;

      if (!m1Val && !m2Val) {
        setError("Veuillez entrer au moins une concentration");
        setResult(null);
        return;
      }

      let res = null;

      if (solveFor === "M2") {
        if (!m1Val || !v1Val || !v2Val) {
          setError("Veuillez entrer M1, V1 et V2");
          setResult(null);
          return;
        }
        res = (m1Val * v1Val * n1Val) / (v2Val * n2Val);
        setM2(res.toFixed(4));
      } else if (solveFor === "M1") {
        if (!m2Val || !v1Val || !v2Val) {
          setError("Veuillez entrer M2, V1 et V2");
          setResult(null);
          return;
        }
        res = (m2Val * v2Val * n2Val) / (v1Val * n1Val);
        setM1(res.toFixed(4));
      } else if (solveFor === "V1") {
        if (!m1Val || !m2Val || !v2Val) {
          setError("Veuillez entrer M1, M2 et V2");
          setResult(null);
          return;
        }
        res = (m2Val * v2Val * n2Val) / (m1Val * n1Val);
        setV1(res.toFixed(4));
      } else if (solveFor === "V2") {
        if (!m1Val || !m2Val || !v1Val) {
          setError("Veuillez entrer M1, M2 et V1");
          setResult(null);
          return;
        }
        res = (m1Val * v1Val * n1Val) / (m2Val * n2Val);
        setV2(res.toFixed(4));
      }

      setResult({
        calculated: res ? res.toFixed(4) : null,
        variable: solveFor,
      });
    } catch (err) {
      setError("Erreur dans le calcul");
      setResult(null);
    }
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3">
            <div className="mb-4">
              <p>
                La <strong>calculatrice de titration chimique</strong> est utilisée pour calculer la concentration ou le volume des solutions dans les opérations de titrage.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Calculatrice de Titration</h2>
              </div>

              <div className="form-group">
                <label>Calculer</label>
                <select
                  className="form-control"
                  value={solveFor}
                  onChange={(e) => setSolveFor(e.target.value)}
                >
                  <option value="M2">Concentration du titré (M2)</option>
                  <option value="M1">Concentration du titrant (M1)</option>
                  <option value="V1">Volume du titrant (V1)</option>
                  <option value="V2">Volume du titré (V2)</option>
                </select>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label>Concentration du titrant M1 (mol/L)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={m1}
                    onChange={(e) => setM1(e.target.value)}
                    placeholder="M1"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>Volume du titrant V1 (mL)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={v1}
                    onChange={(e) => setV1(e.target.value)}
                    placeholder="V1"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label>Concentration du titré M2 (mol/L)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={m2}
                    onChange={(e) => setM2(e.target.value)}
                    placeholder="M2"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>Volume du titré V2 (mL)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={v2}
                    onChange={(e) => setV2(e.target.value)}
                    placeholder="V2"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label>Moles du titrant (n1)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={n1}
                    onChange={(e) => setN1(e.target.value)}
                    placeholder="1"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>Moles du titré (n2)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={n2}
                    onChange={(e) => setN2(e.target.value)}
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12">
                  <button className="btn btn-success" onClick={calculate}>
                    Calculer
                  </button>
                </div>
              </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {result && (
              <div className="p-3 mb-3 rounded bg-info">
                <h3 className="text-white">Résultat :</h3>
                <p className="text-white">
                  <strong>{result.variable} = {result.calculated}</strong>
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>
      <br />
    </div>
  );
}
export default TitrationCalculator;
